import React, { Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../../components/Header';
import { useFinanceiroAtleta } from '../../../hooks/useFinanceiroAtleta';
import { usePendenciasFinanceiras } from '../../../hooks/usePendenciasFinanceiras';
import { FiltroPeriodo, BlocoPendencias } from '../../../components/financeiro';
import ResumoTab from './ResumoTab';
import ExtratoTab from './ExtratoTab';
import ReembolsosTab from './ReembolsosTab';
import type { ExtratoLinha } from '../../../types/financeiro';

// Page de orquestração: lê e escreve a aba ativa em ?tab= via useSearchParams (com
// replace: true, para que trocar de aba não empilhe histórico), renderiza FiltroPeriodo acima
// das abas, monta ResumoTab/ExtratoTab e o bloco Pendências. Pendências vive no hub, não na aba
// (Phase 4, `## Notes` do plano) — uma pendência não deve desaparecer porque o atleta foi olhar
// outra aba, então fica acima de `<ul className="nav nav-tabs">`, com consulta própria via
// usePendenciasFinanceiras (não deriva da página carregada do extrato). ResumoTab/ExtratoTab/
// ReembolsosTab montam por baixo do FiltroPeriodo/BlocoPendencias, sem tocar no idioma ARIA das
// abas (tablist/presentation/tab) consolidado na Phase 4.

function rotaPendencia(linha: ExtratoLinha): string {
  return linha.tipo === 'AGENDAMENTO'
    ? `/quadras/pedido/${linha.referenciaId}`
    : `/athlete/payment/${linha.referenciaId}`;
}

type TabId = 'resumo' | 'extrato' | 'reembolsos';

const TABS: { id: TabId; label: string }[] = [
  { id: 'resumo', label: 'Resumo' },
  { id: 'extrato', label: 'Extrato' },
  { id: 'reembolsos', label: 'Reembolsos' },
];

const FinanceiroHub: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // ?tab= ausente ou desconhecido cai em Resumo, sem quebrar (AC-A5).
  const activeTab: TabId = TABS.some((tab) => tab.id === tabParam) ? (tabParam as TabId) : 'resumo';

  const irParaAba = (id: TabId) => setSearchParams({ tab: id }, { replace: true });

  const navigate = useNavigate();
  const { periodo, setPeriodo, resumo, serie, loading, error, resumoIndisponivel, refetch } =
    useFinanceiroAtleta();
  const { pendencias } = usePendenciasFinanceiras(periodo);

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />

        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="mb-3">
                  <h3 className="fw-700 mb-0">Financeiro</h3>
                </div>

                <FiltroPeriodo periodo={periodo} onChange={setPeriodo} />

                <BlocoPendencias
                  pendencias={pendencias}
                  onPagar={(linha) => navigate(rotaPendencia(linha))}
                />

                <ul className="nav nav-tabs mb-3" role="tablist">
                  {TABS.map((tab) => (
                    <li className="nav-item" key={tab.id} role="presentation">
                      <button
                        type="button"
                        className={`nav-link${activeTab === tab.id ? ' active' : ''}`}
                        onClick={() => irParaAba(tab.id)}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>

                {activeTab === 'resumo' && (
                  <ResumoTab
                    resumo={resumo}
                    serie={serie}
                    loading={loading}
                    error={error}
                    resumoIndisponivel={resumoIndisponivel}
                    onRetry={refetch}
                    periodo={periodo}
                  />
                )}
                {activeTab === 'extrato' && <ExtratoTab periodo={periodo} />}
                {activeTab === 'reembolsos' && <ReembolsosTab />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default FinanceiroHub;
